import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { BedDouble, Plus, Search, FileSpreadsheet, Pencil, Trash2, AlertTriangle, Download } from "lucide-react";
import EntityFormDialog from "@/components/EntityFormDialog";
import ImportExcelModal from "@/components/ImportExcelModal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const TIPOLOGIE = ["Standard", "Junior Suite", "Suite", "Family", "Single", "Double", "Twin"];

export default function RoomsTab({ hotel, currentUser }) {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterPiano, setFilterPiano] = useState("__all__");
  const [filterTipo, setFilterTipo] = useState("__all__");

  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState({ numero: "", piano: "", tipologia: "", note: "" });
  const [saving, setSaving] = useState(false);

  const [importOpen, setImportOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);

  const canManage = currentUser.role === "super_admin";

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/hotels/${hotel.id}/rooms`, { params: { attiva: true } });
      setRooms(r.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [hotel.id]);

  const piani = useMemo(
    () => Array.from(new Set(rooms.map((r) => r.piano).filter(Boolean))).sort(),
    [rooms],
  );
  const tipi = useMemo(
    () => Array.from(new Set(rooms.map((r) => r.tipologia).filter(Boolean))).sort(),
    [rooms],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rooms.filter((r) => {
      if (q && !r.numero.toLowerCase().includes(q)) return false;
      if (filterPiano !== "__all__" && (r.piano || "") !== filterPiano) return false;
      if (filterTipo !== "__all__" && (r.tipologia || "") !== filterTipo) return false;
      return true;
    });
  }, [rooms, search, filterPiano, filterTipo]);

  const openCreate = () => {
    setEditTarget(null);
    setForm({ numero: "", piano: "", tipologia: "", note: "" });
    setFormOpen(true);
  };

  const openEdit = (room) => {
    setEditTarget(room);
    setForm({
      numero: room.numero,
      piano: room.piano || "",
      tipologia: room.tipologia || "",
      note: room.note || "",
    });
    setFormOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.numero.trim()) {
      toast.error("Il numero camera è obbligatorio");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        numero: form.numero.trim(),
        piano: form.piano.trim() || null,
        tipologia: form.tipologia.trim() || null,
        note: form.note.trim() || null,
      };
      if (editTarget) {
        await api.patch(`/hotels/${hotel.id}/rooms/${editTarget.id}`, payload);
        toast.success("Camera aggiornata");
      } else {
        await api.post(`/hotels/${hotel.id}/rooms`, payload);
        toast.success("Camera creata");
      }
      setFormOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  const submitRemove = async () => {
    if (!removeTarget) return;
    try {
      await api.delete(`/hotels/${hotel.id}/rooms/${removeTarget.id}`);
      toast.success("Camera rimossa");
      setRemoveTarget(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const expected = hotel.numero_camere || 0;
  const configured = rooms.length;
  const isEmpty = configured === 0;
  const incomplete = configured > 0 && configured < expected;

  return (
    <div className="space-y-4" data-testid="rooms-tab">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-sm">
            <span className="font-medium" data-testid="rooms-count">{configured}</span>
            <span className="text-muted-foreground"> / {expected} camere configurate</span>
          </div>
          {incomplete && (
            <div className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-1 rounded-full">
              <AlertTriangle className="h-3 w-3" />
              Configura le {expected - configured} camere mancanti per abilitare il Controllo Camere con selezione specifica
            </div>
          )}
        </div>
        {canManage && (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setImportOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full border border-border text-sm hover:bg-accent transition-colors"
              data-testid="rooms-import-btn"
            >
              <FileSpreadsheet className="h-4 w-4" /> Importa da Excel
            </button>
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center gap-1.5 px-4 h-9 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:scale-[1.02] active:scale-[0.98] transition-transform"
              data-testid="rooms-new-btn"
            >
              <Plus className="h-4 w-4" /> Nuova camera
            </button>
          </div>
        )}
      </div>

      {/* Empty state */}
      {!loading && isEmpty && (
        <div className="rounded-2xl border-2 border-dashed border-border/60 p-8 text-center bg-background/30">
          <BedDouble className="h-12 w-12 mx-auto text-muted-foreground/40 mb-3" />
          <div className="font-display text-lg mb-1">Nessuna camera configurata</div>
          <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
            Configura l'anagrafica camere di <strong>{hotel.nome}</strong> per abilitare il Controllo Camere con selezione specifica delle stanze controllate ogni giorno.
          </p>
          {canManage && (
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <button onClick={() => setImportOpen(true)} className="inline-flex items-center gap-1.5 px-4 h-9 rounded-full bg-primary text-primary-foreground text-sm font-medium">
                <FileSpreadsheet className="h-4 w-4" /> Importa da Excel
              </button>
              <button onClick={openCreate} className="inline-flex items-center gap-1.5 px-4 h-9 rounded-full border border-border text-sm">
                <Plus className="h-4 w-4" /> Aggiungi singola camera
              </button>
            </div>
          )}
        </div>
      )}

      {/* Filtri + tabella */}
      {!isEmpty && (
        <div className="rounded-2xl border border-border/60 bg-card/60">
          <div className="p-4 border-b border-border/60 flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca per numero…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="rooms-search"
              />
            </div>
            {piani.length > 0 && (
              <Select value={filterPiano} onValueChange={setFilterPiano}>
                <SelectTrigger className="w-[140px]" data-testid="rooms-filter-piano">
                  <SelectValue placeholder="Piano" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Tutti i piani</SelectItem>
                  {piani.map((p) => <SelectItem key={p} value={p}>Piano {p}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {tipi.length > 0 && (
              <Select value={filterTipo} onValueChange={setFilterTipo}>
                <SelectTrigger className="w-[160px]" data-testid="rooms-filter-tipo">
                  <SelectValue placeholder="Tipologia" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Tutte le tipologie</SelectItem>
                  {tipi.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/60">
                  <th className="px-4 py-2.5">Numero</th>
                  <th className="px-4 py-2.5">Piano</th>
                  <th className="px-4 py-2.5">Tipologia</th>
                  <th className="px-4 py-2.5">Note</th>
                  {canManage && <th className="px-4 py-2.5 w-20 text-right">Azioni</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-border/40 hover:bg-accent/30 transition-colors" data-testid={`room-row-${r.id}`}>
                    <td className="px-4 py-2.5 font-medium">{r.numero}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{r.piano || "—"}</td>
                    <td className="px-4 py-2.5">
                      {r.tipologia ? (
                        <span className="inline-block px-2 py-0.5 rounded-full text-[11px] bg-lavender-50 dark:bg-lavender-900/20 text-lavender-700 dark:text-lavender-300">{r.tipologia}</span>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground text-xs max-w-xs truncate">{r.note || "—"}</td>
                    {canManage && (
                      <td className="px-4 py-2.5 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => openEdit(r)} className="h-8 w-8 rounded-lg hover:bg-accent flex items-center justify-center" data-testid={`room-edit-${r.id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => setRemoveTarget(r)} className="h-8 w-8 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/20 hover:text-rose-500 flex items-center justify-center" data-testid={`room-remove-${r.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={canManage ? 5 : 4} className="px-4 py-6 text-center text-sm text-muted-foreground">Nessuna camera corrisponde ai filtri</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {loading && <div className="text-xs text-muted-foreground">Caricamento camere…</div>}

      {/* Form create/edit */}
      <EntityFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        title={editTarget ? "Modifica camera" : "Nuova camera"}
        description={`Anagrafica camera per ${hotel.nome}`}
        size="md"
        onSubmit={submit}
        submitLabel={editTarget ? "Salva" : "Crea camera"}
        saving={saving}
        testId="room-form"
        submitTestId="room-form-submit"
      >
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>Numero *</Label>
            <Input
              data-testid="room-numero"
              required
              placeholder="Es. 101, 205A, Suite Tower 1"
              value={form.numero}
              onChange={(e) => setForm({ ...form, numero: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label>Piano</Label>
            <Input
              data-testid="room-piano"
              placeholder="Es. 1, Terrazza"
              value={form.piano}
              onChange={(e) => setForm({ ...form, piano: e.target.value })}
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Tipologia</Label>
          <Select value={form.tipologia || "__none__"} onValueChange={(v) => setForm({ ...form, tipologia: v === "__none__" ? "" : v })}>
            <SelectTrigger data-testid="room-tipologia"><SelectValue placeholder="Seleziona o lascia vuoto" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— Nessuna —</SelectItem>
              {TIPOLOGIE.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Note <span className="text-muted-foreground text-xs">(opzionale)</span></Label>
          <Textarea
            data-testid="room-note"
            placeholder="Es. Vista mare, piano executive, balconato…"
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            rows={2}
          />
        </div>
      </EntityFormDialog>

      {/* Import modal */}
      {importOpen && (
        <ImportExcelModal
          entity="rooms"
          hotelId={hotel.id}
          hotelNome={hotel.nome}
          onClose={() => setImportOpen(false)}
          onComplete={() => { setImportOpen(false); load(); }}
        />
      )}

      {/* Remove confirm */}
      <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent className="ih-glass-strong">
          <AlertDialogHeader>
            <AlertDialogTitle>Rimuovere la camera {removeTarget?.numero}?</AlertDialogTitle>
            <AlertDialogDescription>
              La camera verrà disattivata. Eventuali controlli passati che la referenziano resteranno integri.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={submitRemove} className="bg-rose-500 hover:bg-rose-600 text-white">Rimuovi</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
